import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Caption1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { personDisplayName, type Person } from '@vigeo/shared';
import { shellMedia, shellMetrics, tokens, useShellTier } from '@vigeo/ui';
import { PersonCard, PersonName, ReadinessCell, StageTag, nextLine } from './PersonCard.js';
import type { ReadinessEntry } from './usePeopleReadiness.js';

const useStyles = makeStyles({
  /** The reference table sits on a white sheet (Figma 136:3935); same card geometry as everything else. */
  wrapper: {
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
  },
  /**
   * Column model. Fluent's table is `table-layout: fixed`, so widths come from these `col`
   * elements and never from content: they stay put while readiness resolves, and nothing can
   * paint across a column boundary. Identity takes the largest share (more below the desktop
   * tier, where Email has left); tag and verdict columns fit their longest label; role, next
   * step and email share the remainder.
   */
  colIdentity: { width: shellMetrics.listIdentityColumn, [shellMedia.belowTablet]: { width: shellMetrics.listIdentityColumnCompact } },
  colRole: { width: shellMetrics.listRoleColumn },
  colTag: { width: shellMetrics.listTagColumn },
  colVerdict: { width: shellMetrics.listVerdictColumn },
  /** Reference rhythm: header row 42, body rows 52, 10px cell inset, hairline dividers. */
  headRow: { height: shellMetrics.tableHeaderRow },
  headCell: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightSemibold,
    paddingInline: tokens.spacingHorizontalMNudge,
  },
  row: {
    height: shellMetrics.tableRow,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  cell: { paddingInline: tokens.spacingHorizontalMNudge, verticalAlign: 'middle' },
  /** The avatar keeps its size; the name link shrinks and truncates on one line (full name in its tooltip). */
  identity: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
  truncate: { display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  /** Prose wraps to two lines inside the 52px row, then clips; the full text is in the tooltip. */
  clamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  /** Column reduction: contact detail leaves first, below the desktop tier. */
  wideOnly: { [shellMedia.belowTablet]: { display: 'none' } },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

export interface CandidateTableProps {
  readonly people: readonly Person[];
  readonly roleOf: (person: Person) => string;
  readonly entryOf: (id: string) => ReadinessEntry;
}

const isControl = (target: EventTarget | null) => target instanceof Element && target.closest('a, button') !== null;

/**
 * The pipeline list.
 *
 * Every column is a field of the person record or a projection of that person's derived
 * checklist — the same query the Person Overview reads, so a verdict here is never a second
 * source of truth. Below the tablet tier the rows become the shared person card.
 */
export function CandidateTable({ people, roleOf, entryOf }: CandidateTableProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const tier = useShellTier();

  if (tier === 'mobile') {
    return (
      <div className={styles.stack} role="list" aria-label="People">
        {people.map((person) => (
          <div key={person.id} role="listitem">
            <PersonCard person={person} role={roleOf(person)} entry={entryOf(person.id)} />
          </div>
        ))}
      </div>
    );
  }

  const open = (person: Person) => (event: MouseEvent<HTMLTableRowElement>) => {
    // The name is the accessible link; the rest of the row is a convenience for the mouse.
    if (isControl(event.target)) return;
    void navigate(`/app/people/${person.id}`);
  };

  return (
    <div className={styles.wrapper}>
      <Table size="medium" aria-label="People">
        <colgroup>
          <col className={styles.colIdentity} />
          <col className={styles.colRole} />
          <col className={styles.colTag} />
          <col className={styles.colVerdict} />
          <col />
          <col className={styles.wideOnly} />
        </colgroup>
        <TableHeader>
          <TableRow className={styles.headRow}>
            <TableHeaderCell className={styles.headCell}>Person</TableHeaderCell>
            <TableHeaderCell className={styles.headCell}>Role</TableHeaderCell>
            <TableHeaderCell className={styles.headCell}>Stage</TableHeaderCell>
            <TableHeaderCell className={styles.headCell}>Readiness</TableHeaderCell>
            <TableHeaderCell className={styles.headCell}>Next step</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(styles.headCell, styles.wideOnly)}>Email</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {people.map((person) => {
            const entry = entryOf(person.id);
            const line = nextLine(entry);
            const role = roleOf(person);
            return (
              <TableRow key={person.id} className={styles.row} onClick={open(person)}>
                <TableCell className={styles.cell}>
                  <div className={styles.identity}>
                    <Avatar name={personDisplayName(person.name)} size={28} color="neutral" aria-hidden />
                    <PersonName person={person} compact />
                  </div>
                </TableCell>
                <TableCell className={styles.cell}>
                  <Caption1 className={styles.clamp} title={role}>
                    {role}
                  </Caption1>
                </TableCell>
                <TableCell className={styles.cell}>
                  <StageTag state={person.lifecycle} />
                </TableCell>
                <TableCell className={styles.cell}>
                  <ReadinessCell entry={entry} />
                </TableCell>
                <TableCell className={styles.cell}>
                  <Caption1 className={mergeClasses(styles.clamp, entry.status === 'ready' ? undefined : styles.quiet)} title={line}>
                    {line}
                  </Caption1>
                </TableCell>
                <TableCell className={mergeClasses(styles.cell, styles.wideOnly)}>
                  <Caption1 className={styles.truncate} title={person.contact.email}>
                    {person.contact.email}
                  </Caption1>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
